import { Model } from 'infra/model'
import { Consultation as ConsultationProto } from '@taxfyle/api-internal/internal/consultation_pb'
import { computed, makeObservable, observable } from 'mobx'
import { Id } from 'types/definitions'
import { match } from 'utils/pattern-match-util'
import { TimeRange } from 'jobs/TimeRange'
import { durationToMinutes } from 'utils/grpc-util'

export enum ConsultationStatus {
  PendingAvailability,
  PendingBooking,
  InProgress,
  Done,
  Cancelled,
}

export default class Consultation extends Model<
  ConsultationProto.AsObject,
  Consultation
> {
  @observable id = Id.none
  @observable jobId = Id.none
  @observable status = ConsultationStatus.PendingAvailability
  @observable times: TimeRange[] = []
  @observable clientUserPublicId = Id.none
  @observable providerUserPublicId = Id.none
  @observable bookedTime?: TimeRange
  @observable requestedLength: number = 30
  @observable skipScheduling: boolean = false

  constructor() {
    super()
    makeObservable(this)
  }

  @computed
  get availability() {
    return this.times?.flatMap((a) =>
      a.decomposeRangeInIntervals(this.requestedLength)
    )
  }

  @computed
  get isPendingBooking() {
    return this.status === ConsultationStatus.PendingBooking
  }

  @computed
  get isPendingAvailability() {
    return this.status === ConsultationStatus.PendingAvailability
  }

  parse(attrs: Partial<ConsultationProto.AsObject>): Partial<Consultation> {
    return {
      id: attrs.id,
      jobId: attrs.jobId,
      status:
        attrs.status !== undefined
          ? match(attrs.status, {
              [ConsultationProto.ConsultationStatus.PENDING_AVAILABILITY]: () =>
                ConsultationStatus.PendingAvailability,
              [ConsultationProto.ConsultationStatus.PENDING_BOOKING]: () =>
                ConsultationStatus.PendingBooking,
              [ConsultationProto.ConsultationStatus.IN_PROGRESS]: () =>
                ConsultationStatus.InProgress,
              [ConsultationProto.ConsultationStatus.DONE]: () =>
                ConsultationStatus.Done,
              [ConsultationProto.ConsultationStatus.CANCELLED]: () =>
                ConsultationStatus.Cancelled,
              [ConsultationProto.ConsultationStatus.UNKNOWN]: () =>
                ConsultationStatus.PendingAvailability,
            })
          : ConsultationStatus.PendingAvailability,
      times: attrs?.availability
        ? attrs.availability?.availabilityTimesList?.map((i) =>
            TimeRange.fromInterval(i)
          )
        : [],
      clientUserPublicId: attrs.clientUserPublicId,
      providerUserPublicId: attrs.providerUserPublicId,
      bookedTime: TimeRange.fromInterval(attrs.bookedTime),
      requestedLength: attrs.requestedLength
        ? durationToMinutes(attrs.requestedLength)
        : 30,
      skipScheduling: attrs.skipScheduling,
    }
  }

  fromJSON(json: Partial<ConsultationProto.AsObject>) {
    return this.set(json, { parse: true })
  }

  static fromJSON(json: Partial<ConsultationProto.AsObject>) {
    return new Consultation().set(json, { parse: true })
  }
}
